import type { IPersonSummary } from "@/pages/Private/redux/search/search.schema";

/** The company a search result is displayed under, ready to render. */
export interface PersonDisplayCompany {
	name: string;
	logoUrl: string | null;
}

type PreviewExperience = NonNullable<IPersonSummary["experiences"]>[number];
type PreviewCompany = NonNullable<PreviewExperience["company"]>;

/** A company's display name, falling back to its domain when the record is unnamed. */
const companyLabel = (company: {
	name?: string | null;
	domain?: string | null;
}): string | undefined => company.name?.trim() || company.domain?.trim() || undefined;

const toDisplayCompany = (
	company: PreviewCompany | null | undefined
): PersonDisplayCompany | null => {
	const name = company ? companyLabel(company) : undefined;

	return name ? { name, logoUrl: company?.logo_url ?? null } : null;
};

/**
 * Picks the company a person is shown under in the profile typeahead (PREC-3903):
 * the current experience that carries a company, then the one the search matched
 * on, then the company global search resolved from that experience's domain.
 *
 * Deliberately never falls back to "the first experience we have" the way the
 * detail-side `currentCompanyIdOf` does — on a preview result that is just as
 * likely to be a job the person left years ago, and a stale employer under a
 * name is worse than no employer at all when the point is telling namesakes apart.
 */
export const personDisplayCompany = (person: IPersonSummary): PersonDisplayCompany | null => {
	const experiences = person.experiences ?? [];

	const fromExperience =
		experiences.find((experience) => experience.is_current && experience.company) ??
		experiences.find((experience) => experience.is_matched && experience.company);

	return toDisplayCompany(fromExperience?.company) ?? toDisplayCompany(person.matchedCompany);
};
